// Kirjala: lisäysilmoituksen tyylit (ks. CartDrawer.showAddedToast)
var CART_TOAST_CSS = `
.cart-toast {
  position: fixed;
  left: 50%;
  bottom: 1.6rem;
  z-index: 2147483000;
  width: calc(100% - 3.2rem);
  max-width: 42rem;
  padding: 1.4rem 4.4rem 1.4rem 1.8rem;
  border: 1px solid rgba(var(--color-foreground), 0.15);
  border-radius: var(--popup-corner-radius, 6px);
  background: rgb(var(--color-background));
  color: rgb(var(--color-foreground));
  box-shadow: 0 0.8rem 2.4rem rgba(0, 0, 0, 0.18);
  opacity: 0;
  visibility: hidden;
  transform: translate(-50%, 2rem);
  transition: opacity 0.25s ease, transform 0.25s ease, visibility 0.25s;
}
.cart-toast--visible {
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, 0);
}
.cart-toast__title {
  margin: 0;
  font-size: 1.5rem;
  line-height: 1.4;
}
.cart-toast__shipping {
  margin: 0.4rem 0 0;
  font-size: 1.3rem;
  line-height: 1.4;
}
.cart-toast__view {
  margin: 0.8rem 0 0;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 1.4rem;
  color: rgb(var(--color-button));
  text-decoration: underline;
  text-underline-offset: 0.3rem;
  cursor: pointer;
}
.cart-toast__close {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  width: 3.6rem;
  height: 3.6rem;
  padding: 0;
  border: 0;
  background: none;
  font-size: 2.4rem;
  line-height: 1;
  color: inherit;
  cursor: pointer;
}
@media (prefers-reduced-motion: reduce) {
  .cart-toast {
    transition: none;
  }
}
`;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.hideAddedToast();
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Kirjala: tuotteen lisäyksen jälkeen näytetään pieni ilmoitus paneelin avaamisen sijaan.
      if (options.silent) {
        this.showAddedToast();
      } else {
        this.open();
      }
    });
  }

  // Kirjala: lisäysilmoitus tuotteen lisäyksen jälkeen. Tekstit ja tyylit ovat tässä tiedostossa.
  // HUOM: teemapäivitys korvaa tämän tiedoston ja product-form.js:n – muutos pitää tehdä uudelleen.
  showAddedToast() {
    const strings = {
      fi: { 'item-added': 'Tuote lisätty ostoskoriisi', 'view-cart': 'Näytä ostoskori', close: 'Sulje' },
      en: { 'item-added': 'Item added to your cart', 'view-cart': 'View cart', close: 'Close' },
      sv: { 'item-added': 'Artikeln har lagts till i varukorgen', 'view-cart': 'Visa varukorg', close: 'Stäng' },
    };
    const lang = (document.documentElement.lang || 'fi').slice(0, 2).toLowerCase();
    const getText = (key) => (strings[lang] || strings.fi)[key];

    if (!document.getElementById('CartToastStyles')) {
      const style = document.createElement('style');
      style.id = 'CartToastStyles';
      style.textContent = CART_TOAST_CSS;
      document.head.appendChild(style);
    }

    let toast = document.getElementById('CartToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'CartToast';
      toast.className = 'cart-toast gradient color-scheme-1';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.addEventListener('mouseenter', () => clearTimeout(this.toastTimer));
      toast.addEventListener('mouseleave', () => this.startToastTimer());
      toast.addEventListener('focusin', () => clearTimeout(this.toastTimer));
      toast.addEventListener('focusout', () => this.startToastTimer());
      document.body.appendChild(toast);
    }
    toast.innerHTML = '';

    const title = document.createElement('p');
    title.className = 'cart-toast__title';
    title.textContent = `✓ ${getText('item-added')}`;
    toast.appendChild(title);

    const shipping = this.querySelector('.free-shipping-bar__text');
    if (shipping) {
      const shippingText = document.createElement('p');
      shippingText.className = 'cart-toast__shipping';
      shippingText.innerHTML = shipping.innerHTML;
      toast.appendChild(shippingText);
    }

    const viewButton = document.createElement('button');
    viewButton.type = 'button';
    viewButton.className = 'cart-toast__view';
    viewButton.textContent = getText('view-cart');
    viewButton.addEventListener('click', () => this.open(document.getElementById('cart-icon-bubble')));
    toast.appendChild(viewButton);

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'cart-toast__close';
    closeButton.setAttribute('aria-label', getText('close'));
    closeButton.innerHTML = '&times;';
    closeButton.addEventListener('click', () => this.hideAddedToast());
    toast.appendChild(closeButton);

    requestAnimationFrame(() => toast.classList.add('cart-toast--visible'));
    this.startToastTimer();
  }

  startToastTimer() {
    clearTimeout(this.toastTimer);
    // Kirjala 4.11.: ilmoitus näkyvissä 12 s (oli 6 s), jotta asiakas ehtii lukea lahjarivit ja reagoida.
    this.toastTimer = setTimeout(() => this.hideAddedToast(), 12000);
  }

  hideAddedToast() {
    clearTimeout(this.toastTimer);
    const toast = document.getElementById('CartToast');
    if (toast) toast.classList.remove('cart-toast--visible');
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
